/**
 * Peak-state freshness (dbo.HeldLeaps_MetricsDaily), as emitted by get_portfolio_view's
 * `peakDataFreshness` block. Shared by PortfolioView and RiskDashboard so the two labels
 * can never drift apart again (they were hand-kept copies until 2026-10-02).
 *
 * Rule carried over from both pages: an absent or malformed block is "unknown" and reads
 * as SUSPECT, never as current.
 *
 * Coverage (backend 2026-10-02): the peak job now SKIPS a line it cannot price from a
 * same-session source, so a run can write some lines and not others. The date-only
 * fields above cannot see that. `heldLines` / `missingLines` / `partial` can: partial ===
 * true means at least one held line has no peak row on the newest date, and the label
 * says how many were refreshed. partial === null means the backend could not tell; we
 * then claim nothing about coverage (never "all refreshed").
 */

export interface PeakFreshness {
  asOf: string | null;
  sessionsBehind: number | null;
  stale: boolean | null;
  status: string | null;
  dueLabel: string | null;
  heldLines: number | null;
  missingLines: number | null;
  partial: boolean | null;
}

export interface PeakFreshnessView {
  /** e.g. "current · 28/28 lines", "current · 27 of 28 lines refreshed", "unknown". */
  label: string;
  /** True for unknown, behind, overdue, awaiting, or a partial run. */
  suspect: boolean;
  asOf: string | null;
}

const num = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

export function parsePeakFreshness(raw: unknown): PeakFreshness | null {
  if (!raw || typeof raw !== "object") return null;
  const pf = raw as Record<string, unknown>;
  return {
    asOf: typeof pf.asOf === "string" ? pf.asOf : null,
    sessionsBehind: num(pf.sessionsBehind),
    stale: typeof pf.stale === "boolean" ? pf.stale : null,
    status: typeof pf.status === "string" ? pf.status : null,
    dueLabel: typeof pf.dueLabel === "string" ? pf.dueLabel : null,
    heldLines: num(pf.heldLines),
    missingLines: num(pf.missingLines),
    partial: typeof pf.partial === "boolean" ? pf.partial : null,
  };
}

function baseLabel(pf: PeakFreshness | null): string {
  if (!pf || pf.stale === null) return "unknown";
  if (pf.status === "overdue") return `today's ${pf.dueLabel ?? "scheduled"} run has not written`;
  if (pf.status === "awaiting_today") return `today's run due ${pf.dueLabel ?? "later"}`;
  if (pf.stale) return pf.sessionsBehind != null ? `${pf.sessionsBehind} sessions behind` : "behind";
  return "current";
}

/** Coverage suffix, or null when the backend could not say (never guess "all"). */
function coverageSuffix(pf: PeakFreshness): string | null {
  const held = pf.heldLines;
  if (held === null || held <= 0 || pf.partial === null) return null;
  if (pf.partial) {
    const missing = pf.missingLines;
    if (missing === null) return "some lines not refreshed";
    const refreshed = Math.max(0, held - missing);
    return `${refreshed} of ${held} lines refreshed`;
  }
  return `${held}/${held} lines`;
}

export function peakFreshnessView(pf: PeakFreshness | null): PeakFreshnessView {
  const base = baseLabel(pf);
  const suffix = pf && base !== "unknown" ? coverageSuffix(pf) : null;
  const suspect = !pf || pf.stale !== false || pf.partial === true;
  return {
    label: suffix ? `${base} · ${suffix}` : base,
    suspect,
    asOf: pf?.asOf ?? null,
  };
}
